import { useState } from "react";
import styled, { useTheme } from "styled-components";
import { animatedBase, popIn, fadeUp } from "../theme/animations";
import texmuIconLight from "../assets/Texmu-iOS-Default-1024x1024@1x.png";
import texmuIconDark from "../assets/Texmu-iOS-Dark-1024x1024@1x.png";

const MAX_BLUR_PX = 12;
const DEFAULT_BLUR_PERCENT = 42;

const percentToPx = (percent) => {
  const value = typeof percent === "number" ? percent : Number(percent) || 0;
  const clamped = Math.min(Math.max(value, 0), 100);
  return Math.round((clamped / 100) * MAX_BLUR_PX);
};

const Wrapper = styled.section`
  max-width: 1120px;
  margin: 0 auto;
  padding: 120px 24px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 64px;
  align-items: center;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    padding: 96px 20px;
  }
`;

const HeroContent = styled.div`
  ${animatedBase};
  animation: ${fadeUp} 0.6s ease forwards;
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.typography.h1};
  margin: 0 0 24px;
  line-height: 1.05;
`;

const Description = styled.p`
  font-size: ${({ theme }) => theme.typography.bodyLarge};
  color: ${({ theme }) => theme.muted};
  margin: 0 0 32px;
  line-height: 1.6;
`;

const CTAButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 16px;
  padding: 16px 28px;
  background: ${({ theme }) => theme.accent};
  color: ${({ theme }) => theme.accentContrast};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: 600;
  font-size: 1.05rem;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 14px 28px rgba(42, 204, 195, 0.28);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 18px 32px rgba(42, 204, 195, 0.32);
  }
`;

const CTAIcon = styled.img`
  width: 36px;
  height: 36px;
  display: block;
  border-radius: 12px;
`;

const PreviewCard = styled.div`
  ${animatedBase};
  animation: ${popIn} 0.6s ease forwards;
  animation-delay: 0.15s;
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: 28px;
  box-shadow: 0 16px 38px rgba(14, 22, 36, 0.12);
  display: grid;
  gap: 22px;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

const HeaderLeft = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 12px;
`;

const PreviewTitle = styled.span`
  font-size: ${({ theme }) => theme.typography.small};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.muted};
`;

const CardHint = styled.span`
  font-size: ${({ theme }) => theme.typography.small};
  font-weight: 600;
  color: ${({ theme }) => theme.accent};
`;

const SliderGroup = styled.div`
  display: grid;
  gap: 10px;
`;

const SliderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;

const SliderLabel = styled.label`
  font-weight: 600;
  color: ${({ theme }) => theme.text};
`;

const SliderValue = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.accent};
`;

const SliderInput = styled.input`
  width: 100%;
  appearance: none;
  height: 4px;
  border-radius: 999px;
  background: ${({ theme }) => theme.border};
  position: relative;
  cursor: pointer;

  &::-moz-range-track {
    height: 4px;
    border-radius: 999px;
    background: ${({ theme }) => theme.border};
  }

  &::-webkit-slider-thumb {
    appearance: none;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: ${({ theme }) => theme.accent};
    border: none;
    box-shadow: 0 0 0 4px rgba(42, 204, 195, 0.25);
    transition: transform 0.2s ease;
  }

  &::-moz-range-thumb {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: ${({ theme }) => theme.accent};
    border: none;
    box-shadow: 0 0 0 4px rgba(42, 204, 195, 0.25);
    transition: transform 0.2s ease;
  }

  &::-webkit-slider-thumb:hover,
  &::-moz-range-thumb:hover {
    transform: scale(1.05);
  }
`;

const ButtonBase = styled.button`
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-weight: 600;
  padding: 10px 16px;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
`;

const ResetButton = styled(ButtonBase)`
  background: ${({ theme }) => theme.background};
  color: ${({ theme }) => theme.text};
  border: 1px solid ${({ theme }) => theme.border};

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 20px rgba(14, 22, 36, 0.12);
  }
`;

const HintText = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.muted};
  font-size: ${({ theme }) => theme.typography.small};
  min-height: 1.4em;
`;

const ResetRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
`;

const PreviewStack = styled.div`
  display: grid;
  gap: 14px;
`;

const PreviewItem = styled.div`
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.background};
  padding: 18px;
  display: grid;
  gap: 10px;
  transition: border-color 0.2s ease, background 0.2s ease, transform 0.2s ease;

  ${({ $interactive, theme }) =>
    $interactive
      ? `cursor: pointer;
         &:hover {
           border-color: ${theme.accent};
           background: ${theme.surface};
           transform: translateY(-1px);
         }
         &:focus-visible {
           outline: 2px solid ${theme.accent};
           outline-offset: 2px;
         }`
      : "cursor: default;"}

  &[data-variant="match"] {
    border-color: rgba(42, 204, 195, 0.24);
  }
`;

const PreviewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;

const PreviewTag = styled.span`
  font-size: ${({ theme }) => theme.typography.small};
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.accent};
`;

const PreviewMeta = styled.span`
  font-size: ${({ theme }) => theme.typography.small};
  color: ${({ theme }) => theme.muted};
`;

const PreviewText = styled.p`
  margin: 0;
  line-height: 1.6;
  filter: ${({ $blurPx }) => ($blurPx > 0 ? `blur(${$blurPx}px)` : "none")};
  transition: filter 0.2s ease;
`;

function Hero() {
  const [blurPercent, setBlurPercent] = useState(DEFAULT_BLUR_PERCENT);
  const [isRevealed, setIsRevealed] = useState(false);
  const theme = useTheme();
  const logoSrc = theme.name === "dark" ? texmuIconDark : texmuIconLight;

  const sliderId = "hero-blur-slider";
  const blurPx = percentToPx(blurPercent);
  const matchedBlurPx = isRevealed ? 0 : blurPx;

  const handleSliderChange = (event) => {
    setBlurPercent(Number(event.target.value));
    setIsRevealed(false);
  };

  const toggleReveal = () => {
    setIsRevealed((prev) => !prev);
  };

  const handleReset = () => {
    setBlurPercent(DEFAULT_BLUR_PERCENT);
    setIsRevealed(false);
  };

  const handlePreviewKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      toggleReveal();
    }
  };

  const hintText = isRevealed
    ? "Matched content is temporarily visible."
    : "Matched content previews use your blur level.";

  return (
    <Wrapper id="hero">
      <HeroContent>
        <Title>Tailored content filtering for a more enjoyable web.</Title>
        <Description>
          Texmu blurs the noise across headlines, posts, and comments so you can focus on what matters. Build your blacklist, group topics, and reveal content only when you choose, with a simple click or tap. Your privacy is protected, since everything is processed locally.
        </Description>
        <CTAButton href="#downloads">
          <span>Download Texmu</span>
          <CTAIcon src={logoSrc} alt="Texmu logo" />
        </CTAButton>
      </HeroContent>
      <PreviewCard>
        <CardHeader>
          <HeaderLeft>
            <PreviewTitle>Preview</PreviewTitle>
          </HeaderLeft>
          <CardHint>Click the blurred text to reveal</CardHint>
        </CardHeader>

        <PreviewStack>
          <PreviewItem
            role="button"
            tabIndex={0}
            data-variant="match"
            aria-pressed={isRevealed}
            aria-label={isRevealed ? "Hide preview" : "Reveal preview"}
            onClick={toggleReveal}
            onKeyDown={handlePreviewKeyDown}
            $interactive
          >
            <PreviewHeader>
              <PreviewTag>Matched content</PreviewTag>
              <PreviewMeta>Contains “giveaway”</PreviewMeta>
            </PreviewHeader>
            <PreviewText $blurPx={matchedBlurPx}>
              Example text mentioning a giveaway so Texmu blurs out this section.
            </PreviewText>
          </PreviewItem>

          <PreviewItem data-variant="clean">
            <PreviewHeader>
              <PreviewTag>Clean content</PreviewTag>
              <PreviewMeta>No match</PreviewMeta>
            </PreviewHeader>
            <PreviewText $blurPx={0}>
              Regular content without blacklist terms remains readable.
            </PreviewText>
          </PreviewItem>
        </PreviewStack>
        <SliderGroup>
          <SliderRow>
            <SliderLabel htmlFor={sliderId}>Blur strength</SliderLabel>
            <SliderValue>{blurPercent}%</SliderValue>
          </SliderRow>
          <SliderInput
            id={sliderId}
            type="range"
            min="0"
            max="100"
            step="1"
            value={blurPercent}
            onChange={handleSliderChange}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={blurPercent}
          />
        </SliderGroup>

        <HintText>{hintText}</HintText>

        <ResetRow>
          <ResetButton type="button" onClick={handleReset}>
            Reset preview
          </ResetButton>
        </ResetRow>
      </PreviewCard>
    </Wrapper>
  );
}

export default Hero;
